/**
 * Local previews (blob URLs) of uploads that left the composer in a sent
 * message. The composer frees its previews once it clears, but the sent
 * bubble still has to show the picked image while its stored copy
 * downloads, so a sent preview is held here by storage key. The oldest is
 * freed once enough newer sends have pushed it out; by then its stored copy
 * has long been on screen.
 */
const KEPT_PREVIEW_LIMIT = 24;

const keptByStorageKey = new Map<string, string>();
const keptUrls = new Set<string>();

type SentUpload = { storageKey?: string | null; previewUrl?: string | null };

/** Hold the previews of the uploads a message was just sent with. */
export function keepSentUploadPreviews(
  uploads: readonly SentUpload[],
  revoke: (url: string) => void = (url) => URL.revokeObjectURL(url)
): void {
  for (const { storageKey, previewUrl } of uploads) {
    if (!storageKey || !previewUrl?.startsWith('blob:')) continue;
    const previous = keptByStorageKey.get(storageKey);
    if (previous && previous !== previewUrl) {
      keptUrls.delete(previous);
      revoke(previous);
    }
    // Re-inserting moves the key to the newest end.
    keptByStorageKey.delete(storageKey);
    keptByStorageKey.set(storageKey, previewUrl);
    keptUrls.add(previewUrl);
  }
  for (const [storageKey, url] of keptByStorageKey) {
    if (keptByStorageKey.size <= KEPT_PREVIEW_LIMIT) break;
    keptByStorageKey.delete(storageKey);
    keptUrls.delete(url);
    revoke(url);
  }
}

/** The local preview a sent upload was picked with, while it is held. */
export function sentUploadPreview(
  storageKey: string | null | undefined
): string | null {
  return (storageKey && keptByStorageKey.get(storageKey)) || null;
}

/** Frees a composer preview unless a sent message has taken it over. */
export function releaseComposerPreview(
  url: string,
  revoke: (url: string) => void = (url) => URL.revokeObjectURL(url)
): void {
  if (!keptUrls.has(url)) revoke(url);
}

/** Test seam: forget every held preview without revoking it. */
export function resetSentUploadPreviewsForTest(): void {
  keptByStorageKey.clear();
  keptUrls.clear();
}
